import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { deriveDisplayedEmployeeStatus } from '@/features/people/lib/employee-status';
import { formatDate } from '@/lib/date';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { useCanModifyEmployee, useIsSuperAdmin, usePermission } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import type { EmployeeListItem } from '@/types/employee';
import { Link } from '@tanstack/react-router';
import { Check, Copy, Eye, MoreHorizontal, Trash2, UserCheck, UserX } from 'lucide-react';
import { useState } from 'react';

interface EmployeeTableProps {
  employees: EmployeeListItem[];
  visibleColumns: string[];
  isLoading?: boolean;
  onImpersonate?: (id: string) => void;
  onDeactivate?: (id: string) => void;
  onDelete?: (id: string) => void;
}

function getInitials(
  firstName: string | null | undefined,
  lastName: string | null | undefined
): string {
  const f = firstName?.charAt(0) || '';
  const l = lastName?.charAt(0) || '';
  return `${f}${l}`.toUpperCase() || '?';
}

// A leaver gets the badge the card view and the profile header show:
// Offboarding until the termination date, Deactivated after it.
function getLeaverBadge(
  employee: Pick<EmployeeListItem, 'status' | 'terminationDate'>,
  className?: string
) {
  switch (deriveDisplayedEmployeeStatus(employee)) {
    case 'offboarding':
      return (
        <Badge
          className={cn(
            'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300',
            className
          )}
        >
          Offboarding
        </Badge>
      );
    case 'deactivated':
      return (
        <Badge variant="destructive" className={className}>
          Deactivated
        </Badge>
      );
    default:
      return null;
  }
}

function getStatusBadge(employee: EmployeeListItem) {
  switch (employee.status) {
    case 'probation':
      return (
        <div className="flex flex-col gap-0.5">
          <Badge variant="warning">Probation</Badge>
          {employee.probationEndDate && (
            <span className="text-xs text-muted-foreground">
              until {formatDate(employee.probationEndDate, 'short')}
            </span>
          )}
        </div>
      );
    case 'terminated':
      return getLeaverBadge(employee);
    case 'onboarding':
      return <Badge variant="secondary">Onboarding</Badge>;
    case 'active':
    default:
      return <Badge variant="default">Active</Badge>;
  }
}

function getEmploymentTypeLabel(type: string | null | undefined): string {
  if (!type) return '—';
  return type;
}

/**
 * Email cell with copy-to-clipboard functionality
 */
function CopyableEmail({ email }: { email: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Failed to copy - silently fail
    }
  };

  return (
    <div className="group flex items-center gap-1 min-w-0">
      <a
        href={`mailto:${email}`}
        className="truncate text-muted-foreground hover:text-foreground hover:underline max-w-[150px]"
        title={email}
      >
        {email}
      </a>
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 rounded-full opacity-0 transition-opacity group-hover:opacity-100"
              onClick={handleCopy}
            >
              {copied ? (
                <Check className="h-3 w-3 s-sage-text" />
              ) : (
                <Copy className="h-3 w-3 text-muted-foreground" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            {copied ? 'Copied!' : 'Copy email'}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

export function EmployeeTable({
  employees,
  visibleColumns,
  isLoading,
  onImpersonate,
  onDeactivate,
  onDelete,
}: EmployeeTableProps) {
  const formatName = useEmployeeNameFormatter();
  const isColumnVisible = (columnId: string) => visibleColumns.includes(columnId);
  const canModify = useCanModifyEmployee();
  const isSuperAdmin = useIsSuperAdmin();
  const canImpersonate = usePermission('auth.impersonate');

  if (isLoading) {
    return <EmployeeTableSkeleton visibleColumns={visibleColumns} />;
  }

  if (employees.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card py-16">
        <div className="text-muted-foreground mb-2 text-lg font-medium">No employees found</div>
        <p className="text-sm text-muted-foreground">Try adjusting your search or filters</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              {isColumnVisible('name') && (
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Name
                </th>
              )}
              {isColumnVisible('position') && (
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Position
                </th>
              )}
              {isColumnVisible('department') && (
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Department
                </th>
              )}
              {isColumnVisible('location') && (
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Location
                </th>
              )}
              {isColumnVisible('manager') && (
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Manager
                </th>
              )}
              {isColumnVisible('startDate') && (
                <th className="w-[120px] px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Start Date
                </th>
              )}
              {isColumnVisible('email') && (
                <th className="w-[180px] px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Email
                </th>
              )}
              {isColumnVisible('employmentType') && (
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Employment Type
                </th>
              )}
              {isColumnVisible('seniorityLevel') && (
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Seniority
                </th>
              )}
              {isColumnVisible('status') && (
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Status
                </th>
              )}
              {isColumnVisible('actions') && <th className="w-10 px-4 py-3"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {employees.map((employee) => (
              <tr key={employee.id} className="transition-colors hover:bg-muted/30">
                {isColumnVisible('name') && (
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Link
                        to="/employees/$id"
                        params={{ id: employee.id }}
                        className="hover:opacity-80 transition-opacity"
                      >
                        <Avatar className="h-8 w-8">
                          {employee.avatarUrl && (
                            <AvatarImage src={employee.avatarUrl} alt={employee.firstName} />
                          )}
                          <AvatarFallback className="text-[10px]">
                            {getInitials(employee.firstName, employee.lastName)}
                          </AvatarFallback>
                        </Avatar>
                      </Link>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Link
                            to="/employees/$id"
                            params={{ id: employee.id }}
                            className="truncate text-sm font-medium text-foreground hover:text-primary hover:underline text-left"
                          >
                            {formatName({ firstName: employee.firstName, lastName: employee.lastName })}
                          </Link>
                          {getLeaverBadge(employee, 'shrink-0')}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          {employee.workEmail}
                        </div>
                      </div>
                    </div>
                  </td>
                )}
                {isColumnVisible('position') && (
                  <td className="px-4 py-3 text-sm text-foreground">
                    {employee.position?.title || <span className="text-muted-foreground">—</span>}
                  </td>
                )}
                {isColumnVisible('department') && (
                  <td className="px-4 py-3">
                    {employee.department?.name ? (
                      <Badge variant="default">{employee.department.name}</Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </td>
                )}
                {isColumnVisible('location') && (
                  <td className="px-4 py-3 text-sm text-foreground">
                    {employee.personalInfo?.address ? (
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="truncate block max-w-[200px] cursor-help">
                              {employee.personalInfo.address}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[300px]">
                            <p className="break-words">{employee.personalInfo.address}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                )}
                {isColumnVisible('manager') && (
                  <td className="px-4 py-3">
                    {(() => {
                      // Use managers array if available, otherwise fallback to manager for backward compatibility
                      const managersToShow =
                        employee.managers && employee.managers.length > 0
                          ? employee.managers
                          : employee.manager
                            ? [employee.manager]
                            : [];

                      if (managersToShow.length === 0) {
                        return <span className="text-sm text-muted-foreground">—</span>;
                      }

                      return (
                        <div className="flex items-center gap-1.5">
                          {managersToShow.map((manager, index) => (
                            <TooltipProvider key={manager.id || index} delayDuration={0}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Link
                                    to="/employees/$id"
                                    params={{ id: manager.id }}
                                    className="hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-full"
                                  >
                                    <Avatar className="h-7 w-7 border-2 border-background shadow-sm">
                                      {manager.avatarUrl && (
                                        <AvatarImage
                                          src={manager.avatarUrl}
                                          alt={`${manager.firstName} ${manager.lastName}`}
                                        />
                                      )}
                                      <AvatarFallback className="text-[9px]">
                                        {getInitials(manager.firstName, manager.lastName)}
                                      </AvatarFallback>
                                    </Avatar>
                                  </Link>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[200px]">
                                  <p className="font-medium">
                                    {formatName({ firstName: manager.firstName, lastName: manager.lastName })}
                                  </p>
                                  {manager.position?.title && (
                                    // The tooltip uses `bg-foreground` + `text-background`
                                    // (dark bg on light theme, light text). `text-muted-foreground`
                                    // is calibrated for the OPPOSITE surface and renders as low-
                                    // contrast grey on grey here. Use `text-background/75` instead
                                    // to get a dimmed-but-readable secondary tone on the dark
                                    // tooltip surface.
                                    <p className="text-xs text-background/75 mt-0.5">
                                      {manager.position.title}
                                    </p>
                                  )}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ))}
                        </div>
                      );
                    })()}
                  </td>
                )}
                {isColumnVisible('startDate') && (
                  <td className="w-[120px] px-4 py-3 text-sm text-foreground whitespace-nowrap">
                    {employee.startDate ? (
                      // en-GB keeps the day-first "22 Jun 2026" order used in this column
                      formatDate(employee.startDate, { day: '2-digit', month: 'short', year: 'numeric' }, 'en-GB')
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                )}
                {isColumnVisible('email') && (
                  <td className="w-[180px] px-4 py-3 text-sm">
                    <CopyableEmail email={employee.workEmail} />
                  </td>
                )}
                {isColumnVisible('employmentType') && (
                  <td className="px-4 py-3 text-sm text-foreground">
                    {employee.employmentTypeRelation?.name ||
                      getEmploymentTypeLabel(employee.employmentType)}
                  </td>
                )}
                {isColumnVisible('seniorityLevel') && (
                  <td className="px-4 py-3 text-sm text-foreground">
                    {employee.seniorityLevel?.name || (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                )}
                {isColumnVisible('status') && (
                  <td className="px-4 py-3">{getStatusBadge(employee)}</td>
                )}
                {isColumnVisible('actions') && (onImpersonate || onDeactivate || (onDelete && isSuperAdmin)) && (
                  <td className="px-4 py-3">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-full text-muted-foreground hover:text-foreground"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-40">
                        {onImpersonate && canImpersonate && employee.status !== 'terminated' && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              onImpersonate(employee.id);
                            }}
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            Login as
                          </DropdownMenuItem>
                        )}
                        {onImpersonate && canImpersonate && employee.status !== 'terminated' && (onDeactivate || (onDelete && isSuperAdmin)) && (
                          <div className="my-1 h-px bg-border" />
                        )}
                        {onDeactivate && (
                          <>
                            {employee.status === 'terminated' ? (
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (canModify) {
                                    onDeactivate(employee.id);
                                  }
                                }}
                                className={cn(
                                  'text-green-600 dark:text-green-400 focus:bg-green-50 dark:focus:bg-green-950 focus:text-green-700 dark:focus:text-green-300',
                                  !canModify && 'opacity-60 cursor-not-allowed hover:bg-transparent'
                                )}
                              >
                                <UserCheck className="mr-2 h-4 w-4" />
                                Reactivate
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                variant="destructive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (canModify) {
                                    onDeactivate(employee.id);
                                  }
                                }}
                                className={cn(
                                  !canModify &&
                                    'opacity-60 cursor-not-allowed hover:bg-transparent hover:text-destructive/60'
                                )}
                              >
                                <UserX className="mr-2 h-4 w-4" />
                                Deactivate
                              </DropdownMenuItem>
                            )}
                          </>
                        )}
                        {onDelete && isSuperAdmin && (
                          <>
                            {onDeactivate && <div className="my-1 h-px bg-border" />}
                            <DropdownMenuItem
                              variant="destructive"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDelete(employee.id);
                              }}
                              className="text-red-600 dark:text-red-400 focus:bg-red-50 dark:focus:bg-red-950 focus:text-red-700 dark:focus:text-red-300"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface EmployeeTableSkeletonProps {
  visibleColumns: string[];
}

function EmployeeTableSkeleton({ visibleColumns }: EmployeeTableSkeletonProps) {
  const isColumnVisible = (columnId: string) => visibleColumns.includes(columnId);

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              {isColumnVisible('name') && (
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Name
                </th>
              )}
              {isColumnVisible('position') && (
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Position
                </th>
              )}
              {isColumnVisible('department') && (
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Department
                </th>
              )}
              {isColumnVisible('location') && (
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Location
                </th>
              )}
              {isColumnVisible('manager') && (
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Manager
                </th>
              )}
              {isColumnVisible('startDate') && (
                <th className="w-[120px] px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Start Date
                </th>
              )}
              {isColumnVisible('email') && (
                <th className="w-[180px] px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Email
                </th>
              )}
              {isColumnVisible('employmentType') && (
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Employment Type
                </th>
              )}
              {isColumnVisible('seniorityLevel') && (
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Seniority
                </th>
              )}
              {isColumnVisible('status') && (
                <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Status
                </th>
              )}
              {isColumnVisible('actions') && <th className="w-10 px-4 py-3"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {Array.from({ length: 10 }).map((_, i) => (
              <tr key={i}>
                {isColumnVisible('name') && (
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div className="space-y-1.5">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-40" />
                      </div>
                    </div>
                  </td>
                )}
                {isColumnVisible('position') && (
                  <td className="px-4 py-3">
                    <Skeleton className="h-4 w-24" />
                  </td>
                )}
                {isColumnVisible('department') && (
                  <td className="px-4 py-3">
                    <Skeleton className="h-5 w-20 rounded-md" />
                  </td>
                )}
                {isColumnVisible('location') && (
                  <td className="px-4 py-3">
                    <Skeleton className="h-4 w-16" />
                  </td>
                )}
                {isColumnVisible('manager') && (
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Skeleton className="h-7 w-7 rounded-full" />
                      <Skeleton className="h-7 w-7 rounded-full" />
                    </div>
                  </td>
                )}
                {isColumnVisible('startDate') && (
                  <td className="w-[120px] px-4 py-3">
                    <Skeleton className="h-4 w-20" />
                  </td>
                )}
                {isColumnVisible('email') && (
                  <td className="w-[180px] px-4 py-3">
                    <Skeleton className="h-4 w-32" />
                  </td>
                )}
                {isColumnVisible('employmentType') && (
                  <td className="px-4 py-3">
                    <Skeleton className="h-4 w-20" />
                  </td>
                )}
                {isColumnVisible('seniorityLevel') && (
                  <td className="px-4 py-3">
                    <Skeleton className="h-4 w-20" />
                  </td>
                )}
                {isColumnVisible('status') && (
                  <td className="px-4 py-3">
                    <Skeleton className="h-5 w-16 rounded-md" />
                  </td>
                )}
                {isColumnVisible('actions') && (
                  <td className="px-4 py-3">
                    <Skeleton className="h-8 w-8 rounded-md" />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
